import { MapViewerNode } from '@3ddv/dvm-internal';
import { Injectable, Injector, inject } from '@angular/core';
import { PriceScaleModel } from 'projects/common/igsu-models/definitions/section.model';
import { BehaviorSubject, Observable, map, take } from 'rxjs';
import { CartService } from '../../_shared/data-access/cart.service';
import { ComputedAvailability, ExchangeService } from '../../_shared/data-access/exchange.service';
import { SectionsService } from '../../_shared/data-access/sections.service';
import { EventsService } from './events.service';

/**
 * PriceScalesService is an injectable service that manages price scale data and selection
 * within a ticketing or event context. It provides functionality to select, unselect,
 * and manage price scales using RxJS's BehaviorSubjects. It interacts with SectionsService
 * to fetch price scale details from sections and EventsTicketsService to calculate
 * total price based on the number of selected tickets and the selected price scale.
 */
@Injectable({
  providedIn: 'root',
})
export class PriceScalesService {
  private _injector = inject(Injector);
  private _eventService = inject(EventsService);
  private _sectionsService = inject(SectionsService);
  private _cartService = inject(CartService);

  private readonly _selectedPriceScale = new BehaviorSubject<string | null>(null);
  public readonly selectedPriceScale$ = this._selectedPriceScale.asObservable();
  /**
   * Retrieves the selected price scale.
   * @returns The current value of selected price scale.
   */
  public getSelectedPriceScale = () => {
    return this._selectedPriceScale.getValue();
  };

  /**
   * Fetches price scales related to a specific map node (section) and emits them as an Observable.
   * @param node The MapViewerNode representing the section to get price scales from.
   * @returns Observable of PriceScaleModel array.
   */
  public getPriceScalesFromNode = (node: MapViewerNode): Observable<PriceScaleModel[]> => {
    return this._sectionsService.availableSectionData$.pipe(
      take(1),
      map(sectionData => sectionData?.find(section => +section.sectionID === +node.id)?.priceScales!)
    );
  };
  /**
   * Selects first price scale from section based on DVM Node
   * @param node The MapViewerNode representing the section to select the price scale.
   */
  public selectPriceScaleFromNode = (node: MapViewerNode) => {
    const provenueSection = this._sectionsService.getSectionFromNodeID(node.id);
    const firstPriceScale = provenueSection?.priceScales[0];

    if (firstPriceScale) {
      this.selectPriceScale(firstPriceScale?.priceScaleID);
      this.updateTotalPrice();
    }
  };

  /**
   * Checks if any price scale is currently selected.
   * @returns A boolean indicating if a price scale is selected.
   */
  public isAnyPriceScaleSelected = () => {
    return this._selectedPriceScale.getValue() !== null;
  };

  /**
   * Checks if a specific price scale is selected.
   * @param priceScaleID The ID of the price scale to check.
   * @returns A boolean indicating if the specified price scale is selected.
   */
  public isPriceScaleSelected = (priceScaleID: string) => {
    return this._selectedPriceScale.getValue() === priceScaleID;
  };

  /**
   * Selects a price scale and updates the BehaviorSubject.
   * @param priceScale The PriceScaleModel to select.
   */
  public selectPriceScale = (priceScale: string) => {
    return this._selectedPriceScale.next(priceScale);
  };

  /**
   * Unselects the currently selected price scale.
   */
  public unselectPriceScale = () => {
    this._selectedPriceScale.next(null);
  };

  /**
   * Toggles the selection state of a price scale. If it is already selected, it will be unselected and vice versa.
   * Additionally, recalculates the total price based on the new selection.
   * @param event The DOM event, used to stop event propagation.
   * @param priceScale The price scale model to toggle.
   */
  public togglePriceScaleSelection = (event: Event, priceScale: { id: string }) => {
    event.preventDefault();
    this.handlePriceScaleSelection(priceScale);
  };

  /**
   * Filters price scales to include only those that have matching buyer types.
   * @param priceScales - The array of price scales to evaluate.
   * @param selectedTicketsBuyerTypes - Buyer types from selected tickets to match against.
   * @return PriceScaleModel[] - An array of price scales that have matching buyer types.
   */
  public filterPriceScalesWithMatchingBuyerTypes(
    priceScales: PriceScaleModel[],
    selectedTicketsBuyerTypes: number[]
  ): PriceScaleModel[] {
    return priceScales?.filter(
      priceScale =>
        priceScale.buyerTypes?.some(
          buyerType =>
            selectedTicketsBuyerTypes?.some(selectedType => +selectedType === +buyerType.buyerType.buyerTypeID)
        )
    );
  }
  /**
   * Updates the tickets to be displayed based on the selected price scale.
   *
   * @param priceScaleId The ID of the currently selected price scale.
   * @returns An array of tickets that match the selection criteria.
   */
  public updateBestAvailableTicketsToDisplay(priceScaleId: string) {
    const exchangeService = this._injector.get(ExchangeService);
    const eventConfig = this._eventService.getEventConfiguration()!;
    const data = exchangeService.getComputedData();
    const section = this._sectionsService.getSelectedSectionData();

    if (!data?.length || !priceScaleId || !section?.sectionID) {
      console.warn('Missing required data or selections.');
      return [];
    }

    // Remove duplicates
    const uniqueData = data.reduce(
      (obj, value) => {
        if (obj.visited.has(value.seatId)) {
          return obj;
        } else {
          obj.visited.add(value.seatId);
          obj.data.push(value);
          return obj;
        }
      },
      { data: [] as ComputedAvailability[], visited: new Set() }
    ).data;

    const ticketsInfo: ComputedAvailability[] = [];

    uniqueData
      .filter(({ sectionId, priceScaleId: scaleId }) => +sectionId === +section.sectionID && +scaleId === +priceScaleId)
      .forEach(ticket => {
        if (eventConfig.shouldPreserveBuyerType) {
          ticketsInfo.push(...Array(ticket.occurrences).fill({ ...ticket }));
        } else if (+ticket.buyerTypeId === +eventConfig.buyerType) {
          ticketsInfo.push(ticket);
        }
      });

    return ticketsInfo;
  }

  /**
   * Handles the selection of a price scale. It updates the selected price scale state and, if a new price scale is selected, it triggers the total price update.
   * @param priceScale The price scale model to be handled.
   */
  private handlePriceScaleSelection(priceScale: { id: string }) {
    this.selectedPriceScale$.pipe(take(1)).subscribe(currentSelectedPriceScale => {
      const newSelectedPriceScale = this.determineNewSelectedPriceScale(currentSelectedPriceScale, priceScale);

      if (newSelectedPriceScale) {
        this.selectPriceScale(newSelectedPriceScale);
        this.updateTotalPrice();
      }
    });
  }

  /**
   * Determines the new selected price scale based on the current selection. If the same price scale is selected, it returns null, effectively unselecting it.
   * @param currentSelectedPriceScale The currently selected price scale.
   * @param priceScale The price scale model to compare with the current selection.
   * @returns The new selected price scale or null if the same price scale is selected again.
   */
  private determineNewSelectedPriceScale(
    currentSelectedPriceScale: string | null,
    priceScale: { id: string }
  ): string | null {
    if (currentSelectedPriceScale && currentSelectedPriceScale === priceScale.id) {
      return null;
    } else {
      return priceScale.id;
    }
  }

  /**
   * Updates the total price based on the selected tickets and the current exchange rate.
   * It retrieves the necessary services and computes the new total price, setting it via the setTotalPrice method.
   */
  private updateTotalPrice() {
    const exchangeService = this._injector.get(ExchangeService);
    const newPrice = exchangeService.computeBestAvailableTotalPrice();

    this._cartService.setTotalPrice(newPrice || 0);
  }
}
